// Fire-and-forget client-span emitter. The frontend runs ~half the pipeline OUTSIDE the backend —
// candidate fetch (endpoint/searchKey/count), year grounding, and the APPLY step — plus every non-filter
// capability's grounding + execution, so a trace used to stop at the backend interpret. These spans post to
// /telemetry/spans and attach to the SAME trace (the interpret request_id), so the tracing dashboard shows
// the whole flow in one waterfall. This must NEVER throw into the caller and never block the UI — a
// telemetry hiccup must not affect filtering.
//
// FILTER_AI_API_URL is require()d LAZILY inside emitClientSpans rather than imported at module top: config/
// constants.js throws at load when the app's env URLs are unset — fine in the real app (it fails fast via
// service.js), but it would crash any unit test that merely imports a module transitively pulling telemetry
// (e.g. rendering an Otto card, now that FilterAssistant emits execution spans). Telemetry is best-effort,
// so a missing URL degrades to a no-op, never a thrown import.

export function emitClientSpans(requestId, sessionId, spans) {
    if (!requestId || !Array.isArray(spans) || spans.length === 0) return;
    let body;
    try {
        body = JSON.stringify({ request_id: requestId, session_id: sessionId || null, spans });
    } catch (e) {
        return; // unserializable payload -> drop it rather than risk throwing
    }
    try {
        // eslint-disable-next-line global-require
        const { FILTER_AI_API_URL } = require("../../../config/constants");
        // keepalive so the post still completes even if the apply kicks off a heavy dashboard refresh.
        fetch(`${FILTER_AI_API_URL}/telemetry/spans`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            credentials: "include",
            keepalive: true
        }).catch(() => {});
    } catch (e) {
        /* swallow — best-effort (incl. a missing-env constants throw in unit tests) */
    }
}

// Build one client span. Only includes fields that are set, and clamps the error string, to keep the
// payload small and within the backend's ClientSpan limits.
export function makeSpan(name, { input, output, status = "ok", error, latencyMs, metadata } = {}) {
    return {
        name,
        status,
        ...(input !== undefined ? { input } : {}),
        ...(output !== undefined ? { output } : {}),
        ...(error ? { error: String(error).slice(0, 1000) } : {}),
        ...(latencyMs != null ? { latency_ms: Math.max(0, Math.round(latencyMs)) } : {}),
        ...(metadata ? { metadata } : {})
    };
}

// Monotonic clock for span latency (falls back to Date.now where performance is unavailable).
export const nowMs = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());
